import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { updateAvatar } from '../api';
import { secureStorage } from '../utils/secureStorage';
import Navbar from './Navbar';
import './Account.css';

export default function Account({ setToken }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadMessage, setUploadMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    const userData = secureStorage.getUserData();
    if (userData) {
      setUser(userData);
    }
  }, []);

  const handleLogout = () => {
    secureStorage.clearToken();
    setToken(null);
    navigate('/login', { replace: true });
  };

  const handleStatistikClick = () => {
    navigate('/statistik');
  };

  const handleAccountClick = () => {
    // Bereits auf der Account-Seite, nichts zu tun
  };

  const handleBackToDashboard = () => {
    navigate('/dashboard');
  };

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validierung
    if (!file.type.startsWith('image/')) {
      setUploadMessage('Bitte wählen Sie eine Bilddatei aus.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB Limit
      setUploadMessage('Die Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    setIsUploading(true);
    setUploadMessage('');

    try {
      // Konvertiere zu Base64
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Data = e.target.result;
        
        try {
          const response = await updateAvatar({ avatar: base64Data });
          const updatedUser = response.data.user;
          
          // Update secure storage
          secureStorage.setUserData(updatedUser);
          setUser(updatedUser);
          setUploadMessage('Avatar erfolgreich aktualisiert!');
          
          // Reset file input
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        } catch (error) {
          console.error('Fehler beim Hochladen des Avatars:', error);
          setUploadMessage('Fehler beim Hochladen des Avatars.');
        } finally {
          setIsUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Fehler beim Lesen der Datei:', error);
      setUploadMessage('Fehler beim Lesen der Datei.');
      setIsUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setIsUploading(true);
    setUploadMessage('');

    try {
      const response = await updateAvatar({ avatar: null });
      const updatedUser = response.data.user;
      
      // Update secure storage
      secureStorage.setUserData(updatedUser);
      setUser(updatedUser);
      setUploadMessage('Avatar erfolgreich entfernt!');
    } catch (error) {
      console.error('Fehler beim Entfernen des Avatars:', error);
      setUploadMessage('Fehler beim Entfernen des Avatars.');
    } finally {
      setIsUploading(false);
    }
  };

  const getInitials = (username) => {
    return username ? username.charAt(0).toUpperCase() : '?';
  };

  if (!user) {
    return <div className="account-loading">Lade Benutzerdaten...</div>;
  }

  return (
    <div className="account-page">
      <div className="background" />
      
      <Navbar 
        title="Account"
        onLogout={handleLogout}
        logoClickable={true}
        onLogoClick={handleBackToDashboard}
        onStatistikClick={handleStatistikClick}
        onAccountClick={handleAccountClick}
      />

      <div className="account-content">
        <div className="account-container">
          <div className="account-header">
            <h2>Mein Account</h2>
            <p>Verwalten Sie Ihre persönlichen Informationen</p>
          </div>

          <div className="account-sections">
            {/* Avatar Section */}
            <div className="account-section">
              <h3>Profilbild</h3>
              <div className="avatar-section">
                <div className="current-avatar">
                  {user.avatar ? (
                    <img 
                      src={user.avatar} 
                      alt="Profilbild" 
                      className="avatar-image"
                    />
                  ) : (
                    <div className="avatar-initials">
                      {getInitials(user.username)}
                    </div>
                  )}
                </div>
                
                <div className="avatar-actions">
                  <button 
                    className="avatar-upload-btn"
                    onClick={handleAvatarClick}
                    disabled={isUploading}
                  >
                    {isUploading ? (
                      <div className="upload-spinner">
                        <div className="spinner"></div>
                      </div>
                    ) : (
                      'Avatar hochladen'
                    )}
                  </button>
                  
                  {user.avatar && (
                    <button 
                      className="avatar-remove-btn"
                      onClick={handleRemoveAvatar}
                      disabled={isUploading}
                    >
                      Avatar entfernen
                    </button>
                  )}
                  
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  
                  {uploadMessage && (
                    <div className={`upload-message ${uploadMessage.includes('Fehler') ? 'error' : 'success'}`}>
                      {uploadMessage}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* User Info Section */}
            <div className="account-section">
              <h3>Benutzerinformationen</h3>
              <div className="user-info">
                <div className="info-row">
                  <label>Benutzername:</label>
                  <span>{user.username}</span>
                </div>
                
                {user.email && (
                  <div className="info-row">
                    <label>E-Mail:</label>
                    <span>{user.email}</span>
                  </div>
                )}
                
                <div className="info-row">
                  <label>Benutzer-ID:</label>
                  <span>{user.id}</span>
                </div>
              </div>
            </div>

            {/* Account Actions Section */}
            <div className="account-section">
              <h3>Account-Aktionen</h3>
              <div className="account-actions">
                <button 
                  className="action-btn secondary"
                  onClick={handleBackToDashboard}
                >
                  Zurück zum Dashboard
                </button>
                
                <button 
                  className="action-btn danger"
                  onClick={handleLogout}
                >
                  Abmelden
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
